import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "react-hot-toast"
import { IoAddCircleOutline } from "react-icons/io5"
import { MdNavigateNext } from "react-icons/md"
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi"
import { useDispatch, useSelector } from "react-redux"

import {
  createSection,
  updateSection,
} from "../../../../../services/operations/courseDetailsAPI"
import { setCourse, setEditCourse, setStep } from "../../../../../slices/courseSlice"
import IconBtn from "../../../../common/IconBtn"
import NestedView from "./NestedView"

export default function CourseBuilderForm() {
  const { register, handleSubmit, setValue, formState: { errors } } = useForm()
  const { course } = useSelector((state) => state.course)
  const { token } = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [editSectionName, setEditSectionName] = useState(null)
  const dispatch = useDispatch()

  const onSubmit = async (data) => {
    setLoading(true)
    const result = editSectionName
      ? await updateSection({ sectionName: data.sectionName, sectionId: editSectionName, courseId: course._id }, token)
      : await createSection({ sectionName: data.sectionName, courseId: course._id }, token)
    if (result) {
      dispatch(setCourse(result))
      setEditSectionName(null)
      setValue("sectionName", "")
    }
    setLoading(false)
  }

  const cancelEdit = () => { setEditSectionName(null); setValue("sectionName", "") }

  const handleChangeEditSectionName = (sectionId, sectionName) => {
    if (editSectionName === sectionId) { cancelEdit(); return }
    setEditSectionName(sectionId)
    setValue("sectionName", sectionName)
  }

  const goToNext = () => {
    if (!course.courseContent.length) { toast.error("Add at least one section before continuing"); return }
    if (course.courseContent.some((s) => !s.subSection.length)) { toast.error("Every section needs at least one lecture"); return }
    dispatch(setStep(3))
  }

  const sectionCount = course.courseContent?.length ?? 0
  const lectureCount = course.courseContent?.reduce((n, s) => n + s.subSection.length, 0) ?? 0

  return (
    <div className="space-y-5 rounded-2xl border border-richblack-700 bg-richblack-800 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-richblack-5 sm:text-xl">Course Builder</h2>
        {sectionCount > 0 && (
          <span className="flex items-center gap-1.5 rounded-full bg-caribbeangreen-300/10 px-3 py-1 text-xs font-medium text-caribbeangreen-300">
            <FiCheckCircle size={12} />
            {sectionCount} section{sectionCount !== 1 ? "s" : ""} · {lectureCount} lecture{lectureCount !== 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* Add / Edit Section form */}
      <form onSubmit={handleSubmit(onSubmit)} className="rounded-xl border border-richblack-600 bg-richblack-700/50 p-3 sm:p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-richblack-400">
          {editSectionName ? "Edit Section Name" : "Add a Section"}
        </p>

        <input
          disabled={loading}
          placeholder="e.g. Introduction to React"
          {...register("sectionName", { required: true })}
          className="form-style w-full"
        />
        {errors.sectionName && (
          <p className="flex items-center gap-1 text-xs text-pink-200">
            <FiAlertCircle size={12} /> Section name is required
          </p>
        )}

        {/* Buttons — stack on mobile */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-xl border border-yellow-50/30 bg-yellow-50/10 px-4 py-2.5 text-sm font-semibold text-yellow-50 transition-all hover:bg-yellow-50/20 disabled:opacity-50"
          >
            <IoAddCircleOutline size={18} />
            {editSectionName ? "Update Section" : "Add Section"}
          </button>
          {editSectionName && (
            <button
              type="button"
              onClick={cancelEdit}
              className="rounded-xl border border-richblack-600 px-4 py-2.5 text-center text-sm text-richblack-400 transition-colors hover:text-richblack-100"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Section tree */}
      {sectionCount > 0
        ? <NestedView handleChangeEditSectionName={handleChangeEditSectionName} />
        : (
          <div className="rounded-xl border border-dashed border-richblack-600 py-10 text-center">
            <p className="text-sm text-richblack-400">No sections yet — add one above to get started</p>
          </div>
        )
      }

      {/* Navigation */}
      <div className="flex items-center justify-between border-t border-richblack-700 pt-4">
        <button
          type="button"
          onClick={() => { dispatch(setStep(1)); dispatch(setEditCourse(true)) }}
          className="rounded-xl border border-richblack-600 px-4 py-2.5 text-sm font-medium text-richblack-300 transition-colors hover:border-richblack-500 hover:text-richblack-100 sm:px-5"
        >
          ← Back
        </button>
        <IconBtn disabled={loading} text="Save & Continue" onclick={goToNext}>
          <MdNavigateNext />
        </IconBtn>
      </div>
    </div>
  )
}
